// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import addAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import { TextArea } from "@insite/mobius/index";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import {
    AccountFormState,
    initialAccountFormState
} from "@stanion/App/Widgets/CustomCreateAccount/CreateAccount.form.module";
import { xMask, xScreen } from "@stanion/XDL/Utilities";
import { CreateForm } from "@stanion/XDL/Utilities/form";
import cloneDeep from "lodash/cloneDeep";
import React, { RefObject, useContext, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    addAccount,
    signIn,
    validatePassword,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
    loadCurrentCountries,
};

const mapStateToProps = (state: ApplicationState & CustomAppState) => {
    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        countries: getCurrentCountries(state),
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CreateAccountStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    mainGridContainer?: GridContainerProps;
    userGridContainer?: GridContainerProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    userNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requirementsGridItem?: GridItemProps;
    requirementsTitle?: TypographyProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    // XNG-Changes
    halfWidthGridItem?: GridItemProps;
    stateGridItem?: GridItemProps;
}

export const createAccountStyles: CreateAccountStyles = {
    form: {
        css: css`
            margin-top: 22px;
            max-width: 840px;
            ${xScreen("lte", "sm")} {
                width: 100%;
                max-width: 100%;
            }
        `,
    },
    userGridContainer: {
        gap: 27,
    },
    passwordGridItem: {
        width: 12,
    },
    subscriptionGridItem: {
        width: 12,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },
    submitGridItem: {
        width: 12,
    },
    halfWidthGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    stateGridItem: {
        width: [6, 6, 3, 3, 3],
    },
};

const styles = createAccountStyles;
const StyledForm = getStyledWrapper("form");

const CreateExistingAccount = ({
    addAccount,
    signIn,
    validateReCaptcha,
    accountSettings,
    homePageUrl,
    loadCurrentCountries,
    countries,
}: Props) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const formRef = useRef<HTMLFormElement>(null);
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();
    const [accountFormState, setAccountFormState] = useState<AccountFormState>(cloneDeep(initialAccountFormState));
    const accountForm = new CreateForm<AccountFormState>(accountFormState, setAccountFormState, {
        accountSettings,
    });

    const history = useHistory();

    useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        }
    }, []);

    const { isSubmitted } = accountForm.state;
    const fields = accountForm?.state.fields;
    const isFormValid = accountForm.validate();

    const { useEmailAsUserName } = accountSettings;

    const onSuccessHandler = () => {
        setErrorMessage("");
        toasterContext.addToast({ body: translate("Account created successfully!"), messageType: "success" });

        if (homePageUrl) {
            history.push(homePageUrl);
        }
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isSubmitted) {
            accountForm.setSubmitted();
        }

        if (isSubmitting) {
            return;
        }

        if (!accountForm.validate() || !fields) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });

        if (!isReCaptchaValid) {
            return;
        }

        setIsSubmitting(true);

        const formData = {
            firstName: fields.firstName.value,
            lastName: fields.lastName.value,
            email: fields.email.value,
            userName: fields.userName.value,
            password: fields.password.value,
            isSubscribed,
            properties: {
                phone: fields.phone.value,
                company: fields.companyName.value,
                city: fields.city.value,
                country: fields.country.value,
                state: fields.state.value,
                zip: fields.zip.value,
                notes: fields.notes.value,
                address1: fields.address1.value,
                address2: fields.address2.value,
                accountNumber: fields.accountNumber.value,
            },
        };

        setIsSubmitting(true);
        addAccount({
            ...formData,
            onSuccess: onSuccessHandler,
            onError: (error: any) => {
                if (homePageUrl && error === siteMessage("SignInInfo_User_IsDeactivated")) {
                    toasterContext.addToast({
                        body: siteMessage("Custom_Registration_SuccessMessage"),
                        messageType: "success",
                    });
                    history.push(homePageUrl);
                    setErrorMessage("");
                    return;
                }
                setErrorMessage(error);
                setIsSubmitting(false);
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        } as any) as any;
    };

    const selectedCountry = countries?.find(country => country.name === fields.country.value);
    return (
        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate ref={formRef}>
            <GridContainer {...styles.userGridContainer}>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={fields.firstName.value}
                        onChange={e => accountForm?.onFieldChange(fields.firstName, e.currentTarget.value)}
                        error={isSubmitted && fields.firstName.error}
                        {...fields.firstName.attrs}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={fields.lastName.value}
                        onChange={e => accountForm?.onFieldChange(fields.lastName, e.currentTarget.value)}
                        error={isSubmitted && fields.lastName.error}
                        {...fields.lastName.attrs}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={fields.email.value}
                        onChange={e => accountForm?.onFieldChange(fields.email, e.currentTarget.value)}
                        error={isSubmitted && fields.email.error}
                        {...fields.email.attrs}
                    />
                </GridItem>
                {!useEmailAsUserName && (
                    <GridItem {...styles.halfWidthGridItem}>
                        <TextField
                            value={fields.userName.value}
                            onChange={e => accountForm?.onFieldChange(fields.userName, e.currentTarget.value)}
                            error={isSubmitted && fields.userName.error}
                            {...fields.userName.attrs}
                        />
                    </GridItem>
                )}
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        type={showPassword ? undefined : "password"}
                        iconProps={{ ...styles.passwordIcon, src: showPassword ? EyeOff : Eye }}
                        iconClickableProps={{
                            onClick: () => {
                                setShowPassword(!showPassword);
                            },
                            type: "button",
                        }}
                        clickableText={fields.password.value}
                        value={fields.password.value}
                        error={isSubmitted && fields.password.error}
                        onChange={e => accountForm?.onFieldChange(fields.password, e.currentTarget.value)}
                        {...fields.password.attrs}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        type={showConfirmPassword ? undefined : "password"}
                        iconProps={{
                            ...styles.confirmPasswordIcon,
                            src: showConfirmPassword ? EyeOff : Eye,
                        }}
                        iconClickableProps={{
                            onClick: () => {
                                setShowConfirmPassword(!showConfirmPassword);
                            },
                            type: "button",
                        }}
                        clickableText={fields.confirmPassword.value}
                        value={fields.confirmPassword.value}
                        onChange={e => accountForm?.onFieldChange(fields.confirmPassword, e.currentTarget.value)}
                        error={isSubmitted && fields.confirmPassword.error}
                        {...fields.confirmPassword.attrs}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={fields.companyName.value}
                        onChange={e => accountForm?.onFieldChange(fields.companyName, e.currentTarget.value)}
                        error={isSubmitted && fields.companyName.error}
                        {...fields.companyName.attrs}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={fields.accountNumber.value}
                        onChange={e => accountForm?.onFieldChange(fields.accountNumber, e.currentTarget.value)}
                        error={isSubmitted && fields.accountNumber.error}
                        {...fields.accountNumber.attrs}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={fields.address1.value}
                        onChange={e => accountForm?.onFieldChange(fields.address1, e.currentTarget.value)}
                        error={isSubmitted && fields.address1.error}
                        {...fields.address1.attrs}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={fields.address2.value}
                        onChange={e => accountForm?.onFieldChange(fields.address2, e.currentTarget.value)}
                        error={isSubmitted && fields.address2.error}
                        {...fields.address2.attrs}
                    />
                </GridItem>

                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={fields.city.value}
                        onChange={e => accountForm?.onFieldChange(fields.city, e.currentTarget.value)}
                        error={isSubmitted && fields.city.error}
                        {...fields.city.attrs}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <Select
                        value={fields.country.value}
                        onChange={e => accountForm?.onFieldChange(fields.country, e.currentTarget.value)}
                        error={isSubmitted && fields.country.error}
                        {...fields.country.attrs}
                    >
                        <option value="">{translate("Select Country")}</option>
                        {countries?.map(country => (
                            <option key={country.id} value={country.name}>
                                {country.name}
                            </option>
                        ))}
                    </Select>
                </GridItem>
                <GridItem {...styles.stateGridItem}>
                    <Select
                        value={fields.state.value}
                        onChange={e => accountForm?.onFieldChange(fields.state, e.currentTarget.value)}
                        error={isSubmitted && fields.state.error}
                        {...fields.state.attrs}
                    >
                        <option value="">{translate("Select State")}</option>
                        {selectedCountry?.states?.map(state => (
                            <option key={state.id} value={state.name}>
                                {state.name}
                            </option>
                        ))}
                    </Select>
                </GridItem>
                <GridItem {...styles.stateGridItem}>
                    <TextField
                        value={fields.zip.value}
                        onChange={e => accountForm?.onFieldChange(fields.zip, e.currentTarget.value)}
                        error={isSubmitted && fields.zip.error}
                        {...fields.zip.attrs}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={xMask.phoneNumberUS(fields.phone.value || "")}
                        onChange={e => accountForm?.onFieldChange(fields.phone, e.currentTarget.value)}
                        error={isSubmitted && fields.phone.error}
                        {...fields.phone.attrs}
                    />
                </GridItem>

                <GridItem {...styles.passwordGridItem}>
                    <TextArea
                        value={fields.notes.value}
                        onChange={e => accountForm?.onFieldChange(fields.notes, e.currentTarget.value)}
                        error={isSubmitted && fields.notes.error}
                        rows={1}
                        {...fields.notes.attrs}
                    />
                </GridItem>
                <GridItem {...styles.subscriptionGridItem}>
                    <CheckboxGroup>
                        <Checkbox
                            onChange={(e, value) => setIsSubscribed(value)}
                            checked={isSubscribed}
                            data-test-selector="createAccount_Subscription"
                        >
                            {siteMessage("SignIn_Sign_Up_Newsletter")}
                        </Checkbox>
                    </CheckboxGroup>
                </GridItem>
                <GridItem {...styles.subscriptionGridItem}>
                    <Typography>{siteMessage("Custom_AccountRegistration_TermsAndConditions")}</Typography>
                </GridItem>
                {errorMessage && (
                    <GridItem data-test-selector="createAccount_ErrorMessage" {...styles.submitErrorGridItem}>
                        <Typography {...styles.submitErrorTitle}>{errorMessage}</Typography>
                    </GridItem>
                )}
                <GridItem {...styles.reCaptchaGridItem}>
                    <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                </GridItem>
                <GridItem {...styles.submitGridItem}>
                    <Button
                        {...styles.submitButton}
                        type="submit"
                        disabled={isSubmitting || (!isFormValid && isSubmitted)}
                        data-test-selector="createAccount_createButton"
                    >
                        {translate("I Accept")}
                    </Button>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CreateExistingAccount)),
    definition: {
        allowedContexts: ["CreateAccountPage"],
        group: "Create Account",
    },
};

export default widgetModule;
